import { CharacterImage } from '../../assets/character'
import { defineComponent, onMounted, ref } from 'vue'
import {
  AggKey,
  CautionLevel,
  cautionLevelToKorName,
  getLevel,
  useStore,
  villainName,
} from './state'

const colors: {
  [K in CautionLevel]: {
    text: string
    bar: string
  }
} = {
  [CautionLevel.serious]: {
    text: '#F98360',
    bar: '#FBA48A',
  },
  [CautionLevel.caution]: {
    text: '#E7AE5B',
    bar: '#F8D893',
  },
  [CautionLevel.normal]: {
    text: '#64919F',
    bar: '#AFC7CB',
  },
  [CautionLevel.healthy]: {
    text: '#6FA377',
    bar: '#D2E1CC',
  },
}

export type ExamResult = {
  character: string
  score: number
}

export default defineComponent({
  name: 'PersonalityGraph',
  setup () {
    const store = useStore()
    const elementRef = ref<HTMLElement>()

    onMounted(() => {
      if (elementRef.value) {
        onMount(elementRef.value)
      }
    })

    return () => {
      return (
        <div
          ref={elementRef}
          class="flex flex-row justify-start content-start items-start p-[1rem]"
        >
          <div class="py-[1em] space-y-[19px] mr-2">
            {store.result.map((result) => {
              return (
                <div class="flex flex-row justify-start align-center items-center h-[25px] leading-[25px] space-x-[0.6em] ">
                  <img
                    src={CharacterImage[result.character]}
                    class={
                      result.character === AggKey.kongkong
                        ? 'w-[28px] -mx-[2px]'
                        : 'w-[24px]'
                    }
                  />
                  <div class="font-bold text-[11px] whitespace-nowrap">
                    {villainName(result.character)}
                  </div>
                </div>
              )
            })}
          </div>
          <div class="relative grow mb-[2em]">
            <div class="border-solid border-[#dbdbdb] border-b-[1px] absolute inset-0 flex flex-row justify-start align-start items-start">
              {[
                CautionLevel.healthy,
                CautionLevel.normal,
                CautionLevel.caution,
                CautionLevel.serious,
              ].map(level => (
                <div
                  class={[
                    'relative h-full border-dashed border-[#dbdbdb] border-l-[1px] first:border-none',
                    level === CautionLevel.healthy
                      ? 'grow-[43]'
                      : level === CautionLevel.normal
                        ? 'grow-[24]'
                        : level === CautionLevel.caution
                          ? 'grow-[18]'
                          : 'grow-[15]',
                  ]}
                  style={{ color: colors[level].text }}
                >
                  <div class="absolute inset-x-0 bottom-[-2em] h-[2em] text-center text-[11px] pt-1">
                    {cautionLevelToKorName(level)}
                  </div>
                </div>
              ))}
            </div>
            <div class="py-[1em] space-y-[19px]">
              {store.result.map((result) => {
                return (
                  <div class="graph-bar-frame relative h-[25px] truncate">
                    <div
                      class="graph-bar-inner absolute inset-0 flex flex-row justify-end align-center items-center px-[1em] rounded-r-[1em] font-bold text-[11px] h-[25px]"
                      data-val={result.score}
                      data-colorlevel={0}
                      data-targetlevel={getLevel(result.score)}
                      style={{
                        backgroundColor: colors[CautionLevel.healthy].bar,
                        transform: 'translateX(-100%)',
                      }}
                    >
                      <div class="text" />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )
    }
  },
})

const onMount = (el: HTMLElement) => {
  const offset = 0.9

  el.querySelectorAll('.graph-bar-inner').forEach((x) => {
    const h = x as HTMLElement
    const v = Number(h.dataset.val)
    h.style.transition = `transform ${(Math.floor(v * 0.2) + 0.45).toFixed(
      1,
    )}s ease-out, background-color .5s`
  })
  setTimeout(() => {
    el.querySelectorAll('.graph-bar-inner').forEach((x) => {
      const h = x as HTMLElement
      const v = Number(h.dataset.val)
      h.style.transform = `translateX(-${(offset * 10 * (10 - v)).toFixed(2)}%)`
    })
    onFrame()
  }, 100)

  let x = 0
  let sum = 0
  let sumSameCount = 0
  const onFrame = () => {
    if (document.contains(el)) {
      if (x++ % 4 === 0) {
        const sum_ = Array.from(el.querySelectorAll('.graph-bar-frame')).reduce(
          (cum, el) => {
            const h = el as HTMLElement
            const hr = h.getBoundingClientRect()
            const c = h.children[0] as HTMLElement
            const cr = c.getBoundingClientRect()
            const pc =
              (cr.x + cr.width - hr.x - hr.width * (1 - offset)) /
              (hr.width * offset)

            ;(c.children[0] as HTMLElement).innerText = (pc * 10).toFixed(1)

            const cl = Number(c.dataset.colorlevel)
            const ct = Number(c.dataset.targetlevel)

            if (pc >= 0.4 && ct >= 1 && cl < 1) {
              const level = (c.dataset.colorlevel = '1')
              c.style.backgroundColor = colors[level].bar
            }
            if (pc >= 0.62 && ct >= 2 && cl < 2) {
              const level = (c.dataset.colorlevel = '2')
              c.style.backgroundColor = colors[level].bar
            }
            if (pc >= 0.75 && ct >= 3 && cl < 3) {
              const level = (c.dataset.colorlevel = '3')
              c.style.backgroundColor = colors[level].bar
            }
            return cum + pc
          },
          0,
        )

        if (sum === sum_) {
          sumSameCount++
        } else {
          sumSameCount = 0
          sum = sum_
        }
      }
      if (sumSameCount > 3) {
        // return console.log('sumSameCount > 5');
      } else {
        window.requestAnimationFrame(onFrame)
      }
    }
  }
}
